import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalysisService } from '../../../services/analysis.service';
import { PowerPlant } from '../../../common/components/solar-calculation-result/model/power-plant';
import { CalculatorResultWrapper } from '../../../common/models/analysis/calculator-result-wrapper';
import { Util } from '../../../common/models/util';
import { inOutAnimation } from '../../../common/animations';
import { UserRole } from '../../../common/models/user/user-role.enum';
import { PageTitleService } from '../../../services/page-title.service';
import { RecalculateData } from '../../../common/components/solar-calculation-result/model/recalculate-data';
import { RecalculateRequest } from '../../../common/models/analysis/recalculate-request';
import { CalculatorResult } from '../../../common/models/analysis/solar-calculator-result';
import { LoaderService } from '../../../services/loader.service';
import { finalize } from 'rxjs';
import { PaymentStatus } from '../../../common/models/payment/payment-status';
import { PowerPlantFormData } from '../../../common/components/solar-calculation-result/model/power-plant-form-data';
import { PriceType } from '../../../common/models/price/price';

@Component({
  selector: 'psc-power-plant-details',
  templateUrl: './power-plant-details.component.html',
  animations: [inOutAnimation({})],
})
export class PowerPlantDetailsComponent implements OnInit {
  powerPlant: PowerPlant;
  resultWrapper: CalculatorResultWrapper;
  recalculated: boolean;

  protected readonly UserRole = UserRole;
  protected readonly PaymentStatus = PaymentStatus;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private analysisService: AnalysisService,
    private pageTitleService: PageTitleService,
    private loaderService: LoaderService,
  ) {
    /**
     * t(title.user.saved-plant-details)
     */
    this.pageTitleService.setTitle('title.user.saved-plant-details');
  }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id')!;

    this.analysisService.getSavedPowerPlant(id).subscribe({
      next: (powerPlant) => {
        console.log(powerPlant);
        this.powerPlant = powerPlant;
        this.setResultWrapper(powerPlant.analysisResponse);
        this.setPowerPlantNamePageTitle();
      },
      error: () => {
        this.router.navigate(['/404']);
      },
    });
  }

  setPowerPlantNamePageTitle() {
    this.pageTitleService.setTitle('title.user.saved-plant-details', { plantName: this.powerPlant.projectName });
  }

  plantRecalculated(data: RecalculateData) {
    const request: RecalculateRequest = {
      analysisId: this.powerPlant.analysisId,
      ...data,
    };
    this.loaderService.showLoader();
    this.analysisService.recalculateSavedPlant(request)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe((result) => {
        const newPlant = JSON.parse(JSON.stringify(this.powerPlant));
        newPlant.analysisResponse = result;
        this.powerPlant = newPlant;
        this.setResultWrapper(result);
        this.recalculated = true;
        this.pageTitleService.setTitle('title.user.unsaved-plant');
        this.scrollToTop();
      });
  }

  setResultWrapper(result: CalculatorResult) {
    this.resultWrapper = new CalculatorResultWrapper(
      Util.productionTypeFromSiteData(this.powerPlant.productionToolName),
      this.powerPlant.consumptionInputMethod,
      result,
      this.powerPlant.analysisRequest);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  updatePowerPlant() {
    this.loaderService.showLoader();

    let locationImage: Blob = new Blob();
    if (this.powerPlant.locationPicture) {
      locationImage = Util.b64toBlob(this.powerPlant.locationPicture);
    }
    const powerPlantFormData: PowerPlantFormData = new PowerPlantFormData({
      projectName: this.powerPlant.projectName,
      investorName: this.powerPlant.investorName,
      address: this.powerPlant.address,
      analysisId: this.powerPlant.analysisId,
    }, this.resultWrapper, locationImage);

    this.analysisService.updatePowerPlant(powerPlantFormData.formData)
      .pipe(finalize(() => this.loaderService.hideLoader()))
      .subscribe(() => {
        this.recalculated = false;
        this.setPowerPlantNamePageTitle();
        this.scrollToTop();
      });
  }

  protected readonly PriceType = PriceType;
}
